import React, { useState, useEffect } from 'react'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { useNavigate, useParams, Link as RouterLink, useLocation } from 'react-router-dom'
import { generateAdminFpUrl } from '../../utils/urls'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import AdminFormActions from '../../components/admin/shared/AdminFormActions'
import AdminFormCard from '../../components/admin/shared/AdminFormCard'
import type { AdminFormTab } from '../../components/admin/shared/AdminFormTabHeader'
import FormErrorSummary from '../../components/admin/shared/FormErrorSummary'
import NoticeBar from '../../components/shared/NoticeBar'
import { useNotify } from '../../components/shared/ToastProvider'
import FpStatusChip, { FP_STATUSES } from '../../components/admin/franchise-partners/FpStatusChip'
import {
  AdminFormSection,
  LabeledTextField,
  LabeledSelect,
  LabeledChipSelect,
  LabeledDateField,
  ToggleField,
  FieldGroupHeading,
} from '../../components/admin/shared/fields'
import { useFormTabs } from '../../hooks/useFormTabs'
import { firstErrorTab } from '../../utils/formErrors'
import {
  Box,
  Container,
  Button,
  MenuItem,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Warning as WarningIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  ManageAccounts as ManageAccountsIcon,
  DatasetLinked as DatasetLinkedIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import ConfirmDialog from '../../components/shared/dialogs/ConfirmDialog'
import FranchisePartnerAccessTab from './FranchisePartnerAccessTab'
import { poolwerxColors } from '../../theme/colors'
import { apiClient } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { isAdmin } from '../../services/auth'
import { FormTabPanel } from '../../components/shared/FormTabPanel'
import { getCountries, getStatesForCountry, isValidStateForCountry, type Country, type State } from '../../services/locations'
import FpResourcesTable from '../../components/admin/FpResourcesTable'
import type { FranchisePartnerResource } from '../../types/resources'

interface FranchisePartnerFormData {
  fp_code: string
  franchise_name: string
  legal_entity_name: string
  fc_trading_name: string
  fc_entity_id: string
  country: string
  state: string
  city: string
  postcode: string
  market: string
  region_code: string
  system_status: string
  fp_status: string
  is_icu: boolean
  is_selling: boolean
  adoption_status: string
  adoption_note: string
  start_date: Date | null
  end_date: Date | null
  evop_go_live_date: Date | null
  franchisee_first_name: string
  franchisee_last_name: string
  franchisee_email: string
  franchisee_mobile: string
  bdm_id: number | null
  tech_contact_name: string
  tech_contact_email: string
  tech_contact_mobile: string
  resources: FranchisePartnerResource[]
  evop_setup_type: string
}

interface BDMOption {
  id: number
  name: string
  email: string
  market: string
}

interface RegionOption {
  region_code: string
  region_name: string
  market: string
  default_bdm_id: number | null
}

const initialFormData: FranchisePartnerFormData = {
  fp_code: '',
  franchise_name: '',
  legal_entity_name: '',
  fc_trading_name: '',
  fc_entity_id: '',
  country: 'AU',
  state: '',
  city: '',
  postcode: '',
  market: 'ANZ',
  region_code: '',
  system_status: 'Provisioning',
  fp_status: 'Onboarding',
  is_icu: false,
  is_selling: false,
  adoption_status: 'Unknown',
  adoption_note: '',
  start_date: null,
  end_date: null,
  evop_go_live_date: null,
  franchisee_first_name: '',
  franchisee_last_name: '',
  franchisee_email: '',
  franchisee_mobile: '',
  bdm_id: null,
  tech_contact_name: '',
  tech_contact_email: '',
  tech_contact_mobile: '',
  resources: [],
  evop_setup_type: ''
}

// Synchronous fallback used both at first render (so MUI's Select
// always has the hydrated country value in its options - no
// "out-of-range value" warning) and as the catch branch when
// getCountries() fails. The async API call replaces this list once
// it lands.
const FALLBACK_COUNTRIES: Country[] = [
  { code: 'AU', name: 'Australia', states: [] },
  { code: 'NZ', name: 'New Zealand', states: [] },
  { code: 'USA', name: 'United States', states: [] },
]

// Field → owning tab slug, used to map backend 422 validation errors onto the
// tab that holds the field (so the error summary can jump there). Labels drive
// the summary's dashed list; anchors focus the field on click where practical.
const FIELD_TO_SLUG: Record<string, string> = {
  franchise_name: 'business', fc_trading_name: 'business', legal_entity_name: 'business',
  fp_code: 'business', fc_entity_id: 'business', fp_status: 'business', start_date: 'business', end_date: 'business',
  market: 'business', region_code: 'business', bdm_id: 'business', country: 'business',
  state: 'business', city: 'business', postcode: 'business',
  franchisee_first_name: 'contacts', franchisee_last_name: 'contacts',
  franchisee_email: 'contacts', franchisee_mobile: 'contacts',
  tech_contact_name: 'contacts', tech_contact_email: 'contacts', tech_contact_mobile: 'contacts',
  system_status: 'system', evop_setup_type: 'system', evop_go_live_date: 'system',
  adoption_status: 'system', adoption_note: 'system',
}
const FP_FIELD_LABELS: Record<string, string> = {
  franchise_name: 'Franchise Name', fc_trading_name: 'FC Trading Name', legal_entity_name: 'FC Legal Entity Name',
  fp_code: 'FP Code', fc_entity_id: 'FC Entity ID', fp_status: 'FP Status', start_date: 'Commencement Date',
  end_date: 'End/Transfer Date',
  market: 'Market', region_code: 'Region', bdm_id: 'ROM/FBC', country: 'Country', state: 'State',
  city: 'City', postcode: 'Postcode/ZIP',
  franchisee_first_name: 'Franchisee First Name', franchisee_last_name: 'Franchisee Last Name',
  franchisee_email: 'Franchisee Email', franchisee_mobile: 'Franchisee Mobile',
  tech_contact_name: 'Tech Contact Name', tech_contact_email: 'Tech Contact Email',
  tech_contact_mobile: 'Tech Contact Mobile',
  system_status: 'System Status', evop_setup_type: 'Setup Type', evop_go_live_date: 'Go Live Date',
  adoption_status: 'Adoption Status', adoption_note: 'Adoption Note',
}
const FP_FIELD_ANCHORS: Record<string, string> = {
  franchise_name: 'fpf-franchise_name',
  fp_code: 'fpf-fp_code',
}

const FranchisePartnerForm: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // Admin FP pages are addressed by the per-tenure DB id (id-only - no
  // fp_code route). The editable code lives in formData.fp_code.
  const { id } = useParams<{ id?: string }>()
  const isEditMode = !!id
  const { user } = useAuth()
  const { notify } = useNotify()

  // Get return URL from location state, fallback to default
  const returnUrl = location.state?.returnUrl || '/admin/franchise-partners'

  // Tab order: the Users tab only renders in edit mode, so its slug is filtered
  // out otherwise. `useFormTabs` derives slug ↔ index and keeps #hash in sync.
  const TAB_SLUGS: ReadonlyArray<{ slug: string; editOnly?: boolean }> = [
    { slug: 'business' },
    { slug: 'contacts' },
    { slug: 'users', editOnly: true },
    { slug: 'system' },
    { slug: 'resources' },
  ]
  const visibleSlugs = TAB_SLUGS.filter(t => isEditMode || !t.editOnly).map(t => t.slug)
  // Legacy ?tab=N bookmarks still land on the right tab when there's no #hash.
  const legacyTab = parseInt(new URLSearchParams(location.search).get('tab') || '0', 10)
  const { tabValue, setTabValue, handleTabChange } = useFormTabs(visibleSlugs, {
    fallbackIndex: isNaN(legacyTab) ? 0 : legacyTab,
  })
  // Resolve each field's owning tab index for the current mode (edit adds Users).
  const fieldToTab: Record<string, number> = Object.fromEntries(
    Object.entries(FIELD_TO_SLUG)
      .map(([field, slug]) => [field, visibleSlugs.indexOf(slug)])
      .filter(([, idx]) => (idx as number) >= 0),
  )

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [formData, setFormData] = useState<FranchisePartnerFormData>(initialFormData)
  // Snapshot of the last-saved state. Compared with `formData` to detect
  // unsaved edits; reset after a successful save.
  const [originalFormData, setOriginalFormData] = useState<FranchisePartnerFormData>(initialFormData)
  const [fpId, setFpId] = useState<number | null>(null)
  const [regions, setRegions] = useState<RegionOption[]>([])
  const [bdmOptions, setBdmOptions] = useState<BDMOption[]>([])
  const [countries, setCountries] = useState<Country[]>(FALLBACK_COUNTRIES)
  const [stateOptions, setStateOptions] = useState<State[]>([])

  // Load franchise partner data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadFranchisePartner()
    }
  }, [isEditMode, id])

  // Load regions and BDM options when market changes
  useEffect(() => {
    loadRegions()
    loadBdmOptions()
  }, [formData.market])

  // Load location data on mount
  useEffect(() => {
    loadLocationData()
  }, [])

  // Load states when country changes
  useEffect(() => {
    if (formData.country) {
      loadStatesForCountry(formData.country)
    }
  }, [formData.country])

  const loadFranchisePartner = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(`/admin/franchise-partners/${id}/edit`)
      const data = response.data
      if (data.id) setFpId(data.id)

      // Sanitize data to ensure no null values for string fields
      const sanitizedData = {
        ...initialFormData, // Start with defaults
        ...data, // Override with actual data
        // Handle specific fields that might be null
        fp_code: data.fp_code || '',
        franchise_name: data.franchise_name || '',
        legal_entity_name: data.legal_entity_name || '',
        fc_trading_name: data.fc_trading_name || '',
        fc_entity_id: data.fc_entity_id != null ? String(data.fc_entity_id) : '',
        country: data.country || 'AU',
        state: data.state || '',
        city: data.city || '',
        postcode: data.postcode || '',
        market: data.market || 'ANZ',
        region_code: data.region_code || '',
        system_status: data.system_status || 'Provisioning',
        fp_status: data.fp_status || 'Onboarding',
        is_icu: data.is_icu ?? false,
        is_selling: data.is_selling ?? false,
        adoption_status: data.adoption_status || 'Unknown',
        adoption_note: data.adoption_note || '',
        start_date: data.start_date ? new Date(data.start_date) : null,
        end_date: data.end_date ? new Date(data.end_date) : null,
        franchisee_first_name: data.franchisee_first_name || '',
        franchisee_last_name: data.franchisee_last_name || '',
        franchisee_email: data.franchisee_email || '',
        franchisee_mobile: data.franchisee_mobile || '',
        bdm_id: data.bdm_id || null,
        tech_contact_name: data.tech_contact_name || '',
        tech_contact_email: data.tech_contact_email || '',
        tech_contact_mobile: data.tech_contact_mobile || '',
        resources: Array.isArray(data.resources) ? data.resources : [],
        evop_setup_type: data.evop_setup_type || '',
        evop_go_live_date: data.evop_go_live_date ? new Date(data.evop_go_live_date) : null
      }
      
      setFormData(sanitizedData)
      // Snapshot for dirty-state detection. Use the same object so the
      // first compare is identity-equal until the user edits something.
      setOriginalFormData(sanitizedData)
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load franchise partner')
    } finally {
      setLoading(false)
    }
  }

  const loadRegions = async () => {
    try {
      const response = await apiClient.get(`/admin/region-options?market=${formData.market}`)
      setRegions(response.data || [])
    } catch (err) {
      console.error('Failed to load regions:', err)
      notify({ message: 'Could not load regions for this market.', severity: 'warning' })
    }
  }

  const loadBdmOptions = async () => {
    try {
      const response = await apiClient.get(`/admin/bdm-options?market=${formData.market}`)
      setBdmOptions(response.data || [])
    } catch (err) {
      console.error('Failed to load BDM options:', err)
      notify({ message: 'Could not load ROM/FBC options for this market.', severity: 'warning' })
    }
  }

  const loadLocationData = async () => {
    try {
      const countriesData = await getCountries()
      setCountries(countriesData)
    } catch (err) {
      console.error('Failed to load location data:', err)
      setCountries(FALLBACK_COUNTRIES)
    }
  }

  const loadStatesForCountry = async (countryCode: string) => {
    try {
      const states = await getStatesForCountry(countryCode)
      setStateOptions(states)
    } catch (err) {
      console.error('Failed to load states:', err)
      setStateOptions([])
      notify({ message: 'Could not load states for this country.', severity: 'warning' })
    }
  }

  const handleFieldChange = (field: keyof FranchisePartnerFormData, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }

      // When country changes, clear state if it's not valid for the new country
      if (field === 'country') {
        // Auto-suggest market based on country (user can still override)
        if (!formData.market) {
          updated.market = ['AU', 'NZ'].includes(value) ? 'ANZ' : 'USA'
        }

        // NZ has no states — always clear; otherwise check validity
        if (value === 'NZ') {
          updated.state = ''
        } else if (prev.state) {
          const checkedCountry = value
          const checkedState = prev.state
          isValidStateForCountry(checkedCountry, checkedState).then(isValid => {
            if (isValid) return
            // Ignore a stale result: the user may have changed country (or the
            // state) again before this resolved - only clear if still relevant.
            setFormData(current =>
              current.country === checkedCountry && current.state === checkedState
                ? { ...current, state: '' }
                : current,
            )
          })
        }
      }

      // When region changes, auto-populate BDM from region's default (only if creating new FP or BDM not already set)
      if (field === 'region_code' && value) {
        const selectedRegion = regions.find(r => r.region_code === value)
        if (selectedRegion?.default_bdm_id && (!prev.bdm_id || !isEditMode)) {
          updated.bdm_id = selectedRegion.default_bdm_id
        }
      }

      // When market changes, clear BDM if it's not valid for the new market
      if (field === 'market') {
        updated.region_code = '' // Clear region as it's market-specific
        updated.bdm_id = null // Clear BDM as it's market-specific
      }

      // End/Transfer Date only applies to Terminated FPs. Clear any stale value
      // when the status moves away from Terminated so it isn't saved behind the
      // disabled field.
      if (field === 'fp_status' && value !== 'Terminated') {
        updated.end_date = null
      }

      return updated
    })
  }

  const handleResourcesChanged = (next: FranchisePartnerResource[]) => {
    setFormData(prev => ({ ...prev, resources: next }))
  }

  // Reusable across admin forms - see hooks/useUnsavedChangesGuard.ts.
  // Suppressed during save so the post-save navigate doesn't race with a
  // stale dirty flag.
  const { confirmDiscard } = useUnsavedChangesGuard(formData, originalFormData, {
    suppressed: saving,
  })

  // Persist any resource rows whose `resource_id` differs from the saved
  // baseline. Returns the updated array to merge back into formData.
  const saveDirtyResources = async (): Promise<FranchisePartnerResource[]> => {
    const originalByCode = new Map(
      originalFormData.resources.map(r => [r.resource_code, r] as const),
    )
    const merged = [...formData.resources]
    for (let i = 0; i < merged.length; i++) {
      const current = merged[i]
      const orig = originalByCode.get(current.resource_code)
      const currentId = current.resource_id?.trim() || null
      const origId = orig?.resource_id ?? null
      if (currentId === origId) continue
      const response = await apiClient.put(
        `/admin/franchise-partners/${id}/resources/${current.resource_code}`,
        { resource_id: currentId },
      )
      merged[i] = response.data as FranchisePartnerResource
    }
    return merged
  }

  // Status-driven required-date rules, enforced client-side. Active FPs need a
  // Commencement Date; Terminated FPs need both Commencement and End/Transfer.
  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {}
    if (formData.fp_status === 'Active' && !formData.start_date) {
      errors.start_date = 'Commencement Date is required when status is Active'
    }
    if (formData.fp_status === 'Terminated') {
      if (!formData.start_date) {
        errors.start_date = 'Commencement Date is required when status is Terminated'
      }
      if (!formData.end_date) {
        errors.end_date = 'End/Transfer Date is required when status is Terminated'
      }
    }
    return errors
  }

  const handleSubmit = async () => {
    // Block the save on client-side rule violations before hitting the API,
    // routing the user to the offending field via the shared error summary.
    const clientErrors = validateForm()
    if (Object.keys(clientErrors).length > 0) {
      setValidationErrors(clientErrors)
      setError(null)
      const tab = firstErrorTab(clientErrors, fieldToTab)
      if (tab !== null) setTabValue(tab)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setSaving(true)
    setError(null)
    setValidationErrors({})

    try {
      // Convert fc_entity_id to number or null for API. Resources are
      // managed via dedicated PUT endpoints, so they never travel on the
      // FP create/update body - we save them in a follow-up loop below.
      const { resources: _resources, ...rest } = formData
      const submitData = {
        ...rest,
        fc_entity_id: formData.fc_entity_id ? parseInt(formData.fc_entity_id, 10) : null,
        adoption_status: formData.adoption_status || 'Unknown',
        start_date: formData.start_date ? formData.start_date.toISOString().split('T')[0] : null,
        end_date: formData.end_date ? formData.end_date.toISOString().split('T')[0] : null,
        evop_go_live_date: formData.evop_go_live_date ? formData.evop_go_live_date.toISOString().split('T')[0] : null,
      }

      if (isEditMode) {
        await apiClient.put(`/admin/franchise-partners/${id}`, submitData)
      } else {
        await apiClient.post('/admin/franchise-partners', submitData)
      }

      // Save dirty resources only in edit mode - resource endpoints
      // require an existing FP.
      let nextResources = formData.resources
      if (isEditMode && id) {
        nextResources = await saveDirtyResources()
      }

      const savedSnapshot = { ...formData, resources: nextResources }
      setFormData(savedSnapshot)
      setOriginalFormData(savedSnapshot)

      setSuccessMessage(isEditMode ? 'Franchise partner updated successfully' : 'Franchise partner created successfully')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setTimeout(() => {
        navigate(returnUrl)
      }, 1500)
    } catch (err: any) {
      const detail = err.response?.data?.detail
      if (Array.isArray(detail)) {
        // Map FastAPI 422 field errors onto our field map so the summary lists
        // them and jumps to the owning tab.
        const fieldErrors: Record<string, string> = {}
        detail.forEach((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'unknown'
          fieldErrors[field] = e.msg
        })
        setValidationErrors(fieldErrors)
        setError(null)
        const tab = firstErrorTab(fieldErrors, fieldToTab)
        if (tab !== null) setTabValue(tab)
      } else {
        setError(detail || err.message || 'Failed to save franchise partner')
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (!confirmDiscard()) return
    navigate(returnUrl)
  }

  // Flag tabs that currently hold a validation error (red dot on the tab).
  const erroredTabs = new Set(
    Object.entries(validationErrors)
      .filter(([, msg]) => Boolean(msg))
      .map(([field]) => fieldToTab[field])
      .filter((tab) => tab !== undefined),
  )
  const tabHasError = (slug: string) => erroredTabs.has(visibleSlugs.indexOf(slug))
  const tabs: AdminFormTab[] = [
    { label: 'Business & Location', icon: <BusinessIcon />, hasError: tabHasError('business') },
    { label: 'Contacts', icon: <PeopleIcon />, hasError: tabHasError('contacts') },
    ...(isEditMode ? [{ label: 'Users', icon: <ManageAccountsIcon /> }] : []),
    { label: 'System', icon: <SettingsIcon />, hasError: tabHasError('system') },
    { label: 'Resources', icon: <DatasetLinkedIcon /> },
  ]

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)

    try {
      await apiClient.patch(`/admin/franchise-partners/${id}/soft-delete`)
      setDeleteDialogOpen(false)
      setSuccessMessage('Franchise partner has been deleted')
      setTimeout(() => {
        navigate(returnUrl)
      }, 1500)
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete franchise partner')
      setDeleteDialogOpen(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mb: 3 }}>
        <AdminPageHeader
          pageKey="franchisePartners"
          dense
          titleOverride={isEditMode ? 'Edit Franchise Partner' : 'Add Franchise Partner'}
          breadcrumbs={[
            { label: 'Admin', to: '/admin' },
            { label: 'Franchise Partners', to: returnUrl },
            ...(isEditMode
              ? [{ label: formData.franchise_name || 'Franchise Partner' }, { label: 'Edit' }]
              : [{ label: 'Add New' }]),
          ]}
          actions={
            isEditMode && isAdmin(user) ? (
              <>
                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={(e) => setActionsAnchor(e.currentTarget)}
                    disabled={saving || deleting}
                    sx={{ color: 'text.secondary' }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={actionsAnchor}
                  open={Boolean(actionsAnchor)}
                  onClose={() => setActionsAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setActionsAnchor(null)
                      setDeleteDialogOpen(true)
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Delete franchise partner" />
                  </MenuItem>
                </Menu>
              </>
            ) : undefined
          }
        />

        <FormErrorSummary
          message={error}
          errors={validationErrors}
          fieldLabels={FP_FIELD_LABELS}
          fieldToTab={fieldToTab}
          fieldAnchors={FP_FIELD_ANCHORS}
          onJumpToTab={setTabValue}
        />

        {successMessage && (
          <NoticeBar severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </NoticeBar>
        )}

        <AdminFormCard tabs={tabs} value={tabValue} onChange={handleTabChange}>
          {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <FormTabPanel idPrefix="franchise" value={tabValue} index={0}>
                    <Grid container spacing={3}>
                      {/* Card 1: Franchise Identity (top-left, 7 cols) */}
                      <AdminFormSection title="Franchise Identity" card gridProps={{ md: 7 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <LabeledTextField
                                id="fpf-franchise_name"
                                label="Franchise Name"
                                required
                                value={formData.franchise_name}
                                onChange={(e) => handleFieldChange('franchise_name', e.target.value)}
                                helperText="The everyday name this FP is known by, eg. Poolwerx Upper North Shore"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="FC Trading Name"
                                value={formData.fc_trading_name}
                                onChange={(e) => handleFieldChange('fc_trading_name', e.target.value)}
                                helperText="Use the PWX Trading Name from Franconnect"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="FC Legal Entity Name"
                                value={formData.legal_entity_name}
                                onChange={(e) => handleFieldChange('legal_entity_name', e.target.value)}
                                helperText="Use the FranConnect Legal Entity Name"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FieldGroupHeading>Identifiers</FieldGroupHeading>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LabeledTextField
                                id="fpf-fp_code"
                                label="FP Code"
                                required
                                value={formData.fp_code}
                                onChange={(e) => handleFieldChange('fp_code', e.target.value)}
                                disabled={isEditMode}
                                helperText={isEditMode ? 'Cannot be changed after creation' : 'Lowercase alphanumeric, 3-8 characters'}
                                InputProps={{
                                  readOnly: isEditMode,
                                  endAdornment: isEditMode && (
                                    <Tooltip title="FP code cannot be changed after creation">
                                      <InfoIcon color="info" />
                                    </Tooltip>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LabeledTextField
                                id="fpf-fc_entity_id"
                                label="FC Entity ID"
                                required
                                value={formData.fc_entity_id}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                                  handleFieldChange('fc_entity_id', value)
                                }}
                                inputProps={{
                                  maxLength: 10,
                                  pattern: '[0-9]*',
                                  inputMode: 'numeric'
                                }}
                                helperText="Numeric only, up to 10 digits"
                              />
                            </Grid>
                          </Grid>
                      </AdminFormSection>

                      {/* Card 2: Status & Lifecycle (top-right, 5 cols) */}
                      <AdminFormSection title="Status & Lifecycle" card gridProps={{ md: 5 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <LabeledChipSelect
                                label="FP Status"
                                required
                                value={formData.fp_status}
                                onChange={(v) => handleFieldChange('fp_status', v)}
                                options={FP_STATUSES.map((s) => ({
                                  value: s,
                                  render: <FpStatusChip status={s} />,
                                }))}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FieldGroupHeading>Active Dates</FieldGroupHeading>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LabeledDateField
                                label="Commencement Date"
                                required={formData.fp_status === 'Active' || formData.fp_status === 'Terminated'}
                                value={formData.start_date}
                                onChange={(date) => handleFieldChange('start_date', date)}
                                helperText={
                                  formData.fp_status === 'Active'
                                    ? 'Required while Active'
                                    : formData.fp_status === 'Terminated'
                                      ? 'Required when Terminated'
                                      : undefined
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LabeledDateField
                                label="End/Transfer Date"
                                required={formData.fp_status === 'Terminated'}
                                value={formData.end_date}
                                onChange={(date) => handleFieldChange('end_date', date)}
                                disabled={formData.fp_status !== 'Terminated'}
                                helperText={
                                  formData.fp_status === 'Terminated' ? 'Required when Terminated' : undefined
                                }
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FieldGroupHeading>Operational Flags</FieldGroupHeading>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <ToggleField
                                label="ICU"
                                checked={formData.is_icu}
                                onChange={(v) => handleFieldChange('is_icu', v)}
                                activeColor="error"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <ToggleField
                                label="Selling"
                                checked={formData.is_selling}
                                onChange={(v) => handleFieldChange('is_selling', v)}
                                activeColor="warning"
                              />
                            </Grid>
                          </Grid>
                      </AdminFormSection>

                      {/* Card 3: Assignment & Location (full width) */}
                      <AdminFormSection title="Assignment & Location" card>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <LabeledSelect
                                label="Market"
                                required
                                value={formData.market}
                                onChange={(e) => handleFieldChange('market', e.target.value)}
                              >
                                <MenuItem value="ANZ">ANZ</MenuItem>
                                <MenuItem value="USA">USA</MenuItem>
                              </LabeledSelect>
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <LabeledSelect
                                label="Region"
                                required
                                value={formData.region_code}
                                onChange={(e) => handleFieldChange('region_code', e.target.value)}
                              >
                                {regions.map(region => (
                                  <MenuItem key={region.region_code} value={region.region_code}>
                                    {region.region_name}
                                  </MenuItem>
                                ))}
                              </LabeledSelect>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <LabeledSelect
                                label="ROM/FBC"
                                required
                                value={formData.bdm_id || ''}
                                onChange={(e) => handleFieldChange('bdm_id', e.target.value ? Number(e.target.value) : null)}
                              >
                                <MenuItem value="">
                                  <em>Select ROM/FBC</em>
                                </MenuItem>
                                {bdmOptions.map(bdm => (
                                  <MenuItem key={bdm.id} value={bdm.id}>
                                    {bdm.name} ({bdm.email})
                                  </MenuItem>
                                ))}
                              </LabeledSelect>
                            </Grid>
                            <Grid item xs={12}>
                              <FieldGroupHeading>Business Address</FieldGroupHeading>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <LabeledSelect
                                label="Country"
                                required
                                value={formData.country}
                                onChange={(e) => handleFieldChange('country', e.target.value)}
                              >
                                {countries.map(country => (
                                  <MenuItem key={country.code} value={country.code}>
                                    {country.name}
                                  </MenuItem>
                                ))}
                              </LabeledSelect>
                            </Grid>
                            {formData.country !== 'NZ' && (
                              <Grid item xs={12} md={3}>
                                <LabeledSelect
                                  label="State"
                                  required
                                  value={formData.state}
                                  onChange={(e) => handleFieldChange('state', e.target.value)}
                                >
                                  {/* Fallback for the brief window between formData hydration
                                      and stateOptions arriving - keeps MUI from logging
                                      "out-of-range value". Collapses out once real options load. */}
                                  {stateOptions.length === 0 && formData.state && (
                                    <MenuItem value={formData.state}>
                                      {formData.state}
                                    </MenuItem>
                                  )}
                                  {stateOptions.map(state => (
                                    <MenuItem key={state.code} value={state.code}>
                                      {state.name || state.code}
                                    </MenuItem>
                                  ))}
                                </LabeledSelect>
                              </Grid>
                            )}
                            <Grid item xs={12} md={formData.country === 'NZ' ? 5 : 4}>
                              <LabeledTextField
                                label="City"
                                value={formData.city}
                                onChange={(e) => handleFieldChange('city', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <LabeledTextField
                                label="Postcode/ZIP"
                                value={formData.postcode}
                                onChange={(e) => handleFieldChange('postcode', e.target.value)}
                              />
                            </Grid>
                          </Grid>
                      </AdminFormSection>
                    </Grid>
                  </FormTabPanel>

                  <FormTabPanel idPrefix="franchise" value={tabValue} index={1}>
                    <Grid container spacing={3}>
                      <AdminFormSection title="Franchisee Information" card gridProps={{ md: 6 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <LabeledTextField
                                label="First Name"
                                value={formData.franchisee_first_name}
                                onChange={(e) => handleFieldChange('franchisee_first_name', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <LabeledTextField
                                label="Last Name"
                                value={formData.franchisee_last_name}
                                onChange={(e) => handleFieldChange('franchisee_last_name', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="Email"
                                type="email"
                                value={formData.franchisee_email}
                                onChange={(e) => handleFieldChange('franchisee_email', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="Mobile"
                                value={formData.franchisee_mobile}
                                onChange={(e) => handleFieldChange('franchisee_mobile', e.target.value)}
                              />
                            </Grid>
                          </Grid>
                      </AdminFormSection>

                      <AdminFormSection title="Technical Contact" card gridProps={{ md: 6 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="Tech Contact Name"
                                value={formData.tech_contact_name}
                                onChange={(e) => handleFieldChange('tech_contact_name', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="Tech Contact Email"
                                type="email"
                                value={formData.tech_contact_email}
                                onChange={(e) => handleFieldChange('tech_contact_email', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="Tech Contact Mobile"
                                value={formData.tech_contact_mobile}
                                onChange={(e) => handleFieldChange('tech_contact_mobile', e.target.value)}
                              />
                            </Grid>
                          </Grid>
                      </AdminFormSection>
                    </Grid>
                  </FormTabPanel>

                  <FormTabPanel idPrefix="franchise" value={tabValue} index={isEditMode ? 3 : 2}>
                    <Grid container spacing={3}>
                      <AdminFormSection title="Evo Platform" card>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <LabeledSelect
                                label="System Status"
                                value={formData.system_status}
                                onChange={(e) => handleFieldChange('system_status', e.target.value)}
                              >
                                <MenuItem value="None">None</MenuItem>
                                <MenuItem value="Provisioning">Provisioning</MenuItem>
                                <MenuItem value="Live">Live</MenuItem>
                                <MenuItem value="Retiring">Retiring</MenuItem>
                                <MenuItem value="Ended">Ended</MenuItem>
                              </LabeledSelect>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <LabeledSelect
                                label="Setup Type"
                                value={formData.evop_setup_type}
                                onChange={(e) => handleFieldChange('evop_setup_type', e.target.value)}
                              >
                                <MenuItem value="Migration">Migration</MenuItem>
                                <MenuItem value="New">New</MenuItem>
                                <MenuItem value="Conversion">Conversion</MenuItem>
                              </LabeledSelect>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <LabeledDateField
                                label="Go Live Date"
                                value={formData.evop_go_live_date}
                                onChange={(date) => handleFieldChange('evop_go_live_date', date)}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <LabeledSelect
                                label="Adoption Status"
                                value={formData.adoption_status}
                                onChange={(e) => handleFieldChange('adoption_status', e.target.value)}
                              >
                                <MenuItem value="Unknown">Unknown</MenuItem>
                                <MenuItem value="None">None</MenuItem>
                                <MenuItem value="Partial">Partial</MenuItem>
                                <MenuItem value="Full">Full</MenuItem>
                              </LabeledSelect>
                            </Grid>
                            <Grid item xs={12}>
                              <LabeledTextField
                                label="Adoption Note"
                                value={formData.adoption_note}
                                onChange={(e) => handleFieldChange('adoption_note', e.target.value)}
                                multiline
                                rows={2}
                                helperText="Optional notes about adoption progress"
                              />
                            </Grid>
                          </Grid>
                      </AdminFormSection>

                      <AdminFormSection title="System Connections" card>
                          {isEditMode ? (
                            <Stack spacing={2}>
                              <Alert severity="info" icon={<InfoIcon />}>
                                System connections are managed separately. Use the button below to configure Lightspeed, Xero, and Skimmer connections.
                              </Alert>
                              <Button
                                variant="outlined"
                                startIcon={<SettingsIcon />}
                                component={RouterLink}
                                to={generateAdminFpUrl(id!, 'connections')}
                                state={{ returnUrl: location.pathname }}
                                sx={{
                                  borderColor: poolwerxColors.teal,
                                  color: poolwerxColors.teal,
                                  '&:hover': {
                                    borderColor: poolwerxColors.teal,
                                    bgcolor: poolwerxColors.grey[50]
                                  }
                                }}
                              >
                                Manage Connections
                              </Button>
                            </Stack>
                          ) : (
                            <Alert severity="info" icon={<InfoIcon />}>
                              System connections can be configured after adding the franchise partner.
                            </Alert>
                          )}
                      </AdminFormSection>
                    </Grid>
                  </FormTabPanel>

                  <FormTabPanel idPrefix="franchise" value={tabValue} index={isEditMode ? 4 : 3}>
                    <Grid container spacing={3}>
                      <AdminFormSection
                        title="Resources"
                        card
                        caption="Linked Google Drive folders, Sheets and platform records for this franchise partner."
                      >
                        {isEditMode && fpId ? (
                          <FpResourcesTable
                            fpId={fpId}
                            resources={formData.resources}
                            onResourcesChanged={handleResourcesChanged}
                          />
                        ) : (
                          <Alert severity="info" icon={<InfoIcon />}>
                            Save the franchise partner first - resources can be linked once an FP code exists.
                          </Alert>
                        )}
                      </AdminFormSection>
                    </Grid>
                  </FormTabPanel>

                  {/* Tab 4: Access (edit mode only) */}
                  {isEditMode && fpId && (
                    <FormTabPanel idPrefix="fp" value={tabValue} index={2}>
                      <FranchisePartnerAccessTab fpCode={formData.fp_code} fpId={fpId} fpName={formData.franchise_name} />
                    </FormTabPanel>
                  )}
                </>
              )}
        </AdminFormCard>

        <AdminFormActions
          saveLabel={isEditMode ? 'Save Changes' : 'Create Franchise Partner'}
          saving={saving}
          saveDisabled={loading || deleting}
          onSave={handleSubmit}
          onCancel={handleCancel}
          cancelDisabled={deleting}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          tone="danger"
          title="Delete Franchise Partner"
          pending={deleting}
          confirmLabel="Delete"
          confirmText={formData.fp_code}
          confirmTextLabel="Type the FP code to confirm"
          body={
            <Typography variant="body2">
              Are you sure you want to delete <strong>{formData.franchise_name}</strong> ({formData.fp_code})?
              This hides the franchise partner from all lists. The data is preserved but inaccessible.
            </Typography>
          }
        />
      </Container>
    </>
  )
}

export default FranchisePartnerForm